import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import styles from "./RolesPage.module.scss";
import { Trash, Eye, PencilSimple } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import DataTable from "../components/DataTable/DataTable";
import LoadingOverlay from "../components/LoadingOverlay/LoadingOverlay";
import TabbedFormLayout from "../components/TabbedFormLayout/TabbedFormLayout";
import { AxiosResponse, AxiosErrorType } from "../types";

interface Organization {
  id: string;
  name: string;
}

interface UserType {
  id: number;
  name: string;
  permissions: Record<string, boolean>;
  issuperadmin: boolean;
}

interface AppAccessLevel {
  id: number;
  name: string;
  permissions: Record<string, boolean>;
}

interface OrgRoleType {
  id: number;
  name: string;
  permissions: Record<string, boolean>;
}

interface AuthContext {
  user: { id: string; issuperadmin: boolean; activeorganizationid?: string };
}

const ITEMS_PER_PAGE = 10;

export default function RolesPage() {
  const navigate = useNavigate();
  const { user } = useAuth() as AuthContext;
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [userTypes, setUserTypes] = useState<UserType[]>([]);
  const [appAccessLevels, setAppAccessLevels] = useState<AppAccessLevel[]>([]);
  const [orgRoleTypes, setOrgRoleTypes] = useState<OrgRoleType[]>([]);
  const [orgRolesPage, setOrgRolesPage] = useState<number>(1);
  const [userTypesPage, setUserTypesPage] = useState<number>(1);
  const [appAccessPage, setAppAccessPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(user.activeorganizationid || "");

  const fetchData = async () => {
    try {
      setError([]);
      const endpoints = [
        api.get<AxiosResponse<Organization[]>>("/core/organization/list").catch((err: AxiosErrorType) => {
          console.error("Failed to fetch organizations:", err.message, err.response?.data);
          setError((prev) => [...prev, `Failed to fetch organizations: ${err.message} (${err.response?.status || "unknown"})`]);
          return { data: [] } as AxiosResponse<Organization[]>;
        }),
        api.post<AxiosResponse<{ message: string; usertypes: UserType[] }>>("/core/role/usertype/get", {}).catch((err: AxiosErrorType) => {
          console.error("Failed to fetch user types:", err.message, err.response?.data);
          setError((prev) => [...prev, `Failed to fetch user types: ${err.message} (${err.response?.status || "unknown"})`]);
          return { data: { message: "", usertypes: [] } } as AxiosResponse<{ message: string; usertypes: UserType[] }>;
        }),
        api.post<AxiosResponse<{ message: string; access_levels: AppAccessLevel[] }>>("/core/role/access-level/get", { user_id: user.id }).catch((err: AxiosErrorType) => {
          console.error("Failed to fetch app access levels:", err.message, err.response?.data);
          setError((prev) => [...prev, `Failed to fetch app access levels: ${err.message} (${err.response?.status || "unknown"})`]);
          return { data: { message: "", access_levels: [] } } as AxiosResponse<{ message: string; access_levels: AppAccessLevel[] }>;
        }),
        api.post<AxiosResponse<{ message: string; roles: OrgRoleType[] }>>("/core/role/orgroletype/get", {}).catch((err: AxiosErrorType) => {
          console.error("Failed to fetch organization role types:", err.message, err.response?.data);
          setError((prev) => [...prev, `Failed to fetch organization role types: ${err.message} (${err.response?.status || "unknown"})`]);
          return { data: { message: "", roles: [] } } as AxiosResponse<{ message: string; roles: OrgRoleType[] }>;
        }),
      ];

      const [orgRes, userTypesRes, accessLevelsRes, orgRoleTypesRes] = await Promise.all(endpoints);

      setOrganizations(user.issuperadmin ? orgRes.data : orgRes.data.filter((org) => org.id === user.activeorganizationid));
      setUserTypes(userTypesRes.data.usertypes || []);
      setAppAccessLevels(accessLevelsRes.data.access_levels || []);
      setOrgRoleTypes(orgRoleTypesRes.data.roles || []);

      console.log("Organizations:", orgRes.data);
      console.log("User Types:", userTypesRes.data.usertypes || []);
      console.log("App Access Levels:", accessLevelsRes.data.access_levels || []);
      console.log("Organization Role Types:", orgRoleTypesRes.data.roles || []);
    } catch (err: any) {
      const errorMessage = err.message || "Failed to load role management data.";
      console.error("Fetch failed:", err.message, err.response?.data);
      setError((prev) => [...prev, `${errorMessage} (${err.response?.status || "unknown"})`]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      fetchData();
    }
    return () => {
      isMounted = false;
    };
  }, [user.issuperadmin, user.activeorganizationid, user.id]);

  const handleDeleteOrgRoleType = async (orgroletypeId: number) => {
    try {
      if (!user.issuperadmin) {
        setError((prev) => [...prev, "Only superadmins can delete organization role types"]);
        return;
      }
      const response = await api.post("/core/role/orgroletype/delete", {
        orgroletype_id: orgroletypeId,
      });
      console.log("Delete organization role type response:", response.data);
      await fetchData();
    } catch (err: any) {
      const errorMessage = err.message || "Failed to delete organization role type";
      console.error("Delete organization role type failed:", err.message, err.response?.data);
      setError((prev) => [...prev, `Failed to delete organization role type: ${errorMessage} (${err.response?.status || "unknown"})`]);
    }
  };

  const handleDeleteUserType = async (usertypeId: number) => {
    try {
      if (!user.issuperadmin) {
        setError((prev) => [...prev, "Only superadmins can delete user types"]);
        return;
      }
      const response = await api.post("/core/role/usertype/delete", {
        usertype_id: usertypeId,
      });
      console.log("Delete user type response:", response.data);
      await fetchData();
    } catch (err: any) {
      const errorMessage = err.message || "Failed to delete user type";
      console.error("Delete user type failed:", err.message, err.response?.data);
      setError((prev) => [...prev, `Failed to delete user type: ${errorMessage} (${err.response?.status || "unknown"})`]);
    }
  };

  const handleDeleteAppAccessLevel = async (accessLevelId: number) => {
    try {
      if (!user.issuperadmin) {
        setError((prev) => [...prev, "Only superadmins can delete app access levels"]);
        return;
      }
      const response = await api.post("/core/role/access-level/delete", {
        access_level_id: accessLevelId,
      });
      console.log("Delete app access level response:", response.data);
      await fetchData();
    } catch (err: any) {
      const errorMessage = err.message || "Failed to delete app access level";
      console.error("Delete app access level failed:", err.message, err.response?.data);
      setError((prev) => [...prev, `Failed to delete app access level: ${errorMessage} (${err.response?.status || "unknown"})`]);
    }
  };

  if (loading) return <LoadingOverlay />;

  if (error.length > 0 && organizations.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          {error.map((err, index) => (
            <div key={index}>{err}</div>
          ))}
        </div>
      </div>
    );
  }

  const orgRoleTypesColumns = [
    {
      label: "Role Type Name",
      accessor: "name",
    },
    {
      label: "Permissions",
      accessor: "permissions",
      render: (row: OrgRoleType) => JSON.stringify(row.permissions),
    },
  ];

  const userTypesColumns = [
    {
      label: "Name",
      accessor: "name",
    },
    {
      label: "Permissions",
      accessor: "permissions",
      render: (row: UserType) => JSON.stringify(row.permissions),
    },
    {
      label: "Superadmin",
      accessor: "issuperadmin",
      render: (row: UserType) => (row.issuperadmin ? "Yes" : "No"),
    },
  ];

  const appAccessColumns = [
    {
      label: "Name",
      accessor: "name",
    },
    {
      label: "Permissions",
      accessor: "permissions",
      render: (row: AppAccessLevel) => JSON.stringify(row.permissions),
    },
  ];

  const orgRoleTypesActions = (orgRoleType: OrgRoleType) => (
    <div className={styles.actions}>
      <button onClick={() => navigate(`/roles/view-org-role/${orgRoleType.id}`)} title="View">
        <Eye size={20} />
      </button>
      <button onClick={() => navigate(`/roles/edit-org-role/${orgRoleType.id}`)} title="Edit">
        <PencilSimple size={20} />
      </button>
      <button
        onClick={() => handleDeleteOrgRoleType(orgRoleType.id)}
        title="Delete"
        className={styles.deleteBtn}
        disabled={!user.issuperadmin}
      >
        <Trash size={20} />
      </button>
    </div>
  );

  const userTypesActions = (userType: UserType) => (
    <div className={styles.actions}>
      <button onClick={() => navigate(`/roles/view-user-type/${userType.id}`)} title="View">
        <Eye size={20} />
      </button>
      <button onClick={() => navigate(`/roles/edit-user-type/${userType.id}`)} title="Edit">
        <PencilSimple size={20} />
      </button>
      <button
        onClick={() => handleDeleteUserType(userType.id)}
        title="Delete"
        className={styles.deleteBtn}
        disabled={!user.issuperadmin}
      >
        <Trash size={20} />
      </button>
    </div>
  );

  const appAccessActions = (accessLevel: AppAccessLevel) => (
    <div className={styles.actions}>
      <button onClick={() => navigate(`/roles/view-app-access/${accessLevel.id}`)} title="View">
        <Eye size={20} />
      </button>
      <button onClick={() => navigate(`/roles/edit-app-access/${accessLevel.id}`)} title="Edit">
        <PencilSimple size={20} />
      </button>
      <button
        onClick={() => handleDeleteAppAccessLevel(accessLevel.id)}
        title="Delete"
        className={styles.deleteBtn}
        disabled={!user.issuperadmin}
      >
        <Trash size={20} />
      </button>
    </div>
  );

  const paginatedOrgRoleTypes = orgRoleTypes.slice((orgRolesPage - 1) * ITEMS_PER_PAGE, orgRolesPage * ITEMS_PER_PAGE);
  const paginatedUserTypes = userTypes.slice((userTypesPage - 1) * ITEMS_PER_PAGE, userTypesPage * ITEMS_PER_PAGE);
  const paginatedAppAccessLevels = appAccessLevels.slice((appAccessPage - 1) * ITEMS_PER_PAGE, appAccessPage * ITEMS_PER_PAGE);

  const orgRoleTypesTotalPages = Math.ceil(orgRoleTypes.length / ITEMS_PER_PAGE);
  const userTypesTotalPages = Math.ceil(userTypes.length / ITEMS_PER_PAGE);
  const appAccessTotalPages = Math.ceil(appAccessLevels.length / ITEMS_PER_PAGE);

  const tabs = [
    {
      label: "Organization Role Types",
      content: (
        <div className={styles.tabContent}>
          <div className={styles.tabHeader}>
            {user.issuperadmin && (
              <select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                className={styles.orgSelect}
              >
                <option value="">Select Organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={() => navigate(`/roles/new-org-role${selectedOrgId ? `?org_id=${selectedOrgId}` : ""}`)}
              disabled={user.issuperadmin && !selectedOrgId}
            >
              + Add Organization Role Type
            </button>
          </div>
          <DataTable
            columns={orgRoleTypesColumns}
            data={paginatedOrgRoleTypes}
            actions={orgRoleTypesActions}
            emptyMessage="No organization role types found."
          />
          {orgRoleTypesTotalPages > 1 && (
            <div className={styles.pagination}>
              {Array.from({ length: orgRoleTypesTotalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  onClick={() => setOrgRolesPage(num)}
                  className={num === orgRolesPage ? styles.activePage : ""}
                >
                  {num}
                </button>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      label: "User Types",
      content: (
        <div className={styles.tabContent}>
          <div className={styles.tabHeader}>
            <button onClick={() => navigate("/roles/new-user-type")}>+ Add User Type</button>
          </div>
          <DataTable
            columns={userTypesColumns}
            data={paginatedUserTypes}
            actions={userTypesActions}
            emptyMessage="No user types found."
          />
          {userTypesTotalPages > 1 && (
            <div className={styles.pagination}>
              {Array.from({ length: userTypesTotalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  onClick={() => setUserTypesPage(num)}
                  className={num === userTypesPage ? styles.activePage : ""}
                >
                  {num}
                </button>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      label: "App Access Levels",
      content: (
        <div className={styles.tabContent}>
          <div className={styles.tabHeader}>
            <button onClick={() => navigate("/roles/new-app-access")}>+ Add App Access Level</button>
          </div>
          <DataTable
            columns={appAccessColumns}
            data={paginatedAppAccessLevels}
            actions={appAccessActions}
            emptyMessage="No app access levels found."
          />
          {appAccessTotalPages > 1 && (
            <div className={styles.pagination}>
              {Array.from({ length: appAccessTotalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  onClick={() => setAppAccessPage(num)}
                  className={num === appAccessPage ? styles.activePage : ""}
                >
                  {num}
                </button>
              ))}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Roles Management</h1>
      </div>
      {error.length > 0 && (
        <div className={styles.error}>
          {error.map((err, index) => (
            <div key={index}>{err}</div>
          ))}
        </div>
      )}
      <TabbedFormLayout tabs={tabs} showActions={false} backType="/dashboard" />
    </div>
  );
}
